import { format } from "date-fns";
import {
  ACTIVITY_EVENT_ACTOR_TYPE,
  ACTIVITY_EVENT_TYPE_CATEGORY,
} from "@forgepoint/shared";
import type {
  ActivityEvent,
  OpportunityActivityEvent,
} from "@forgepoint/shared";
import { dayLabel } from "@/modules/items/itemHistoryLabels";

/** The filters the activity log offers, in display order. */
export const OPPORTUNITY_ACTIVITY_FILTERS = [
  "all",
  "changes",
  "quotes",
  "messages",
  "views",
] as const;

/** Which slice of the activity log is shown. */
export type OpportunityActivityFilter =
  (typeof OPPORTUNITY_ACTIVITY_FILTERS)[number];

/** One event as one line; repeat quote views fold into the latest with a count. */
export interface ActivityLogEventEntry {
  type: "event";
  key: string;
  event: OpportunityActivityEvent;
  viewCount: number;
}

/** An agent turn: its first step as the line, the later steps behind a disclosure. */
export interface ActivityLogTurnEntry {
  type: "turn";
  key: string;
  lead: OpportunityActivityEvent;
  steps: OpportunityActivityEvent[];
}

/** One line of the activity log. */
export type ActivityLogEntry = ActivityLogEventEntry | ActivityLogTurnEntry;

/** One day's lines, newest first. */
export interface ActivityLogDayGroup {
  key: string;
  label: string;
  entries: ActivityLogEntry[];
}

// Agent bookkeeping the thread records as user-role messages, not activity.
const MACHINE_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  "task_status_update",
  "agent_thread_message",
  "wait_timer_completed",
]);
// Agent events closer together than this read as one turn.
const AGENT_TURN_GAP_MS = 60_000;

/**
 * Narrows an activity event to an opportunity event.
 * @param event - Any activity event
 * @returns Whether the event was recorded against an opportunity
 */
export function isOpportunityEvent(
  event: ActivityEvent,
): event is OpportunityActivityEvent {
  return event.entityType === "OPPORTUNITY";
}

/**
 * Builds the activity log: drops agent bookkeeping, applies the filter, groups
 * by day newest first, collapses repeat quote views, and folds agent turns.
 * @param events - The opportunity's events
 * @param filter - The selected filter
 * @param showTeamViews - Whether quote views by team members are shown
 * @param now - Reference point for "Today" and "Yesterday"
 * @returns The day groups
 */
export function buildActivityDays(
  events: readonly OpportunityActivityEvent[],
  filter: OpportunityActivityFilter,
  showTeamViews: boolean,
  now: Date = new Date(),
): ActivityLogDayGroup[] {
  const shown = events
    .filter(
      (event) =>
        !isMachineMessage(event) &&
        matchesFilter(event, filter) &&
        (showTeamViews || !isTeamView(event)),
    )
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const byDay = new Map<string, OpportunityActivityEvent[]>();
  for (const event of shown) {
    const key = format(event.createdAt, "yyyy-MM-dd");
    byDay.set(key, [...(byDay.get(key) ?? []), event]);
  }
  return [...byDay.entries()].map(([key, dayEvents]) => ({
    key,
    label: dayLabel(dayEvents[0].createdAt, now),
    entries: foldAgentTurns(collapseQuoteViews(dayEvents)),
  }));
}

/**
 * Folds one day's repeat views of a quote by the same viewer into the latest
 * view, counting them.
 * @param dayEvents - One day's events, newest first
 * @returns One entry per event, with repeat views folded in
 */
export function collapseQuoteViews(
  dayEvents: readonly OpportunityActivityEvent[],
): ActivityLogEventEntry[] {
  const entries: ActivityLogEventEntry[] = [];
  const viewIndexes = new Map<string, number>();
  for (const event of dayEvents) {
    const viewKey = quoteViewKey(event);
    const index = viewKey === null ? undefined : viewIndexes.get(viewKey);
    if (index !== undefined) {
      entries[index] = {
        ...entries[index],
        viewCount: entries[index].viewCount + 1,
      };
      continue;
    }
    if (viewKey !== null) {
      viewIndexes.set(viewKey, entries.length);
    }
    entries.push({ type: "event", key: event.id, event, viewCount: 1 });
  }
  return entries;
}

/**
 * Folds runs of events by the same agent thread, each within a minute of the
 * next, into one turn led by its first step.
 * @param entries - One day's entries, newest first
 * @returns The entries with each agent turn folded
 */
export function foldAgentTurns(
  entries: readonly ActivityLogEventEntry[],
): ActivityLogEntry[] {
  const runs: ActivityLogEventEntry[][] = [];
  for (const entry of entries) {
    const run = runs[runs.length - 1];
    if (run && continuesTurn(run[run.length - 1].event, entry.event)) {
      run.push(entry);
      continue;
    }
    runs.push([entry]);
  }
  return runs.map((run): ActivityLogEntry => {
    if (run.length === 1) {
      return run[0];
    }
    const [lead, ...steps] = run.map((entry) => entry.event).reverse();
    return { type: "turn", key: `turn:${lead.id}`, lead, steps };
  });
}

function isMachineMessage(event: OpportunityActivityEvent): boolean {
  return (
    event.eventType === "AGENT_USER_MESSAGE" &&
    MACHINE_MESSAGE_TYPES.has(event.eventMetadata.messageType)
  );
}

function isTeamView(event: OpportunityActivityEvent): boolean {
  return (
    event.eventType === "QUOTE_VIEWED" &&
    event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.USER
  );
}

function matchesFilter(
  event: OpportunityActivityEvent,
  filter: OpportunityActivityFilter,
): boolean {
  return filter === "all" || filterOf(event) === filter;
}

// The filter an event belongs to; null when only "All" shows it.
function filterOf(
  event: OpportunityActivityEvent,
): OpportunityActivityFilter | null {
  if (event.eventType === "QUOTE_VIEWED") {
    return "views";
  }
  if (isMessage(event)) {
    return "messages";
  }
  const category = ACTIVITY_EVENT_TYPE_CATEGORY[event.eventType];
  if (category === "opportunity") {
    return "changes";
  }
  if (category === "quote") {
    return "quotes";
  }
  return null;
}

function isMessage(event: OpportunityActivityEvent): boolean {
  if (event.eventType === "AGENT_USER_MESSAGE") {
    return true;
  }
  if (
    event.eventType === "AGENT_ACTION_EXECUTED" ||
    event.eventType === "AGENT_TRIGGER_EXECUTED"
  ) {
    const { emailContent, smsContent } = event.eventMetadata;
    return Boolean(emailContent ?? smsContent);
  }
  return false;
}

// Who viewed which quote; null for any other event.
function quoteViewKey(event: OpportunityActivityEvent): string | null {
  if (event.eventType !== "QUOTE_VIEWED") {
    return null;
  }
  const { quoteId, ipAddress } = event.eventMetadata;
  const viewer = event.actorId ?? event.actorName ?? ipAddress ?? "";
  return `${event.actorType}:${viewer}:${quoteId}`;
}

// Whether the older event continues the newer one's agent turn.
function continuesTurn(
  newer: OpportunityActivityEvent,
  older: OpportunityActivityEvent,
): boolean {
  return (
    newer.actorType === ACTIVITY_EVENT_ACTOR_TYPE.AGENT_SDK_THREAD &&
    older.actorType === ACTIVITY_EVENT_ACTOR_TYPE.AGENT_SDK_THREAD &&
    newer.actorId !== null &&
    newer.actorId === older.actorId &&
    newer.createdAt.getTime() - older.createdAt.getTime() <= AGENT_TURN_GAP_MS
  );
}
